import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import {
  Box, Typography, TextField, Button, Link, Checkbox,
  FormControlLabel, InputAdornment, IconButton, Alert, CircularProgress,
} from "@mui/material";
import FloatingIcons, { LOGIN_ICONS } from "../components/auth/FloatingIcons";
import { Visibility, VisibilityOff, ArrowForward, ArrowBack } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { keyframes } from "@mui/material";

const BLUE       = "#1565C0";
const BLUE_MID   = "#1976D2";
const BLUE_LIGHT = "#42A5F5";
const NAVY       = "#0A1628";
const WHITE      = "#FFFFFF";
const TEXT_DARK  = "#0A1628";
const TEXT_MID   = "#37474F";
const TEXT_MUTED = "#78909C";
const BORDER     = "rgba(21,101,192,0.14)";

// ── Keyframes ─────────────────────────────────────────────────
const cardEnter = keyframes`
  from { opacity:0; transform:translateY(40px) scale(0.97); }
  to   { opacity:1; transform:translateY(0)    scale(1); }
`;
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(18px); }
  to   { opacity:1; transform:translateY(0); }
`;
const morphBg = keyframes`
  0%,100% { border-radius:62% 38% 46% 54%/60% 44% 56% 40%; }
  33%      { border-radius:44% 56% 30% 70%/55% 30% 70% 45%; }
  66%      { border-radius:30% 70% 60% 40%/40% 62% 38% 60%; }
`;

const shimmerBtn = keyframes`
  0%   { left:"-120%"; }
  100% { left:"120%";  }
`;
const underlineDraw = keyframes`
  from { width:0; } to { width:100%; }
`;
const logoFloat = keyframes`
  0%,100% { transform:translateY(0) rotate(0deg);  }
  50%      { transform:translateY(-8px) rotate(4deg); }
`;


const fieldSx = (active: boolean) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: active ? WHITE : "#F4F8FF",
    transition: "background-color .25s ease",
    "& fieldset": {
      borderColor:  active ? BLUE : BORDER,
      borderWidth:  active ? "2px" : "1.5px",
      transition:   "all .25s ease",
    },
    "&:hover fieldset":      { borderColor: BLUE_LIGHT },
    "&.Mui-focused fieldset":{ borderColor: BLUE, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
  "& .MuiInputBase-input":  { color: TEXT_DARK, fontSize: ".95rem", py: "14px" },
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();
  const tl = t.auth.login;
  // The glyph has to follow the reading direction: "back" points right in
  // Arabic, "forward" points left. Layout mirroring is handled by the RTL
  // emotion cache, but icon shapes are not.
  const BackIcon    = isRTL ? ArrowForward : ArrowBack;
  const ForwardIcon = isRTL ? ArrowBack    : ArrowForward;
  const redirectPath   = searchParams.get("redirect") || "/membership/classes";
  const sessionExpired = searchParams.get("reason") === "session_expired";

  const [showPw,  setShowPw]  = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [focus,   setFocus]   = useState("");
  const [mounted, setMounted] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", rememberMe: false });

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const user = await authService.login({ email: form.email, password: form.password });
      login(user);
      navigate(redirectPath);
    } catch (err: any) {
      setError(err.message || tl.failed);
    } finally { setLoading(false); }
  };

  return (
    <>
    <Helmet>
      <title>{tl.metaTitle}</title>
      <meta name="description" content={tl.metaDescription} />
      <link rel="canonical" href="https://drpawleb.com/login" />
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <HreflangTags />
    <Box sx={{
      minHeight: "100vh",
      background: `linear-gradient(145deg, ${NAVY} 0%, #0D2137 45%, #0A2040 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden", px: 2, py: 6,
    }}>

      {/* ── Background grid ── */}
      <Box sx={{
        position:"absolute", inset:0, zIndex:0, opacity:.04,
        backgroundImage:`linear-gradient(rgba(255,255,255,.8) 1px,transparent 1px),
                         linear-gradient(90deg,rgba(255,255,255,.8) 1px,transparent 1px)`,
        backgroundSize:"52px 52px",
      }}/>

      {/* ── Morphing orbs ── */}
      {[
        { top:"-12%", left:"-6%",  w:380, h:380, dur:"16s", c:"rgba(21,101,192,.2)"  },
        { top:"55%",  right:"-8%", w:300, h:300, dur:"20s", c:"rgba(66,165,245,.14)" },
        { top:"20%",  left:"60%",  w:200, h:200, dur:"13s", c:"rgba(21,101,192,.10)" },
      ].map((o,i) => (
        <Box key={i} sx={{
          position:"absolute", top:o.top, left:o.left, right:o.right,
          width:o.w, height:o.h, zIndex:0,
          background:`radial-gradient(circle,${o.c} 0%,transparent 70%)`,
          animation:`${morphBg} ${o.dur} ease-in-out infinite ${i%2?"reverse":""}`,
          animationDelay:`${i*2}s`,
        }}/>
      ))}

      {/* ── Back button ── */}
      <Button
        onClick={() => navigate("/")}
        startIcon={<BackIcon sx={{ fontSize: "1rem" }} />}
        sx={{
          position: "absolute", top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 },
          zIndex: 2, color: WHITE, textTransform: "none", fontWeight: 600,
          fontSize: ".88rem", borderRadius: "50px", px: 2.5, py: 1,
          border: "1px solid rgba(255,255,255,0.18)",
          backgroundColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(8px)",
          transition: "all 0.25s ease",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.35)" },
        }}
      >
        {t.auth.backHome}
      </Button>

      {/* ── Language switcher — mirrors to the opposite corner in RTL ── */}
      <Box sx={{ position: "absolute", top: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 }, zIndex: 2 }}>
        <LanguageSwitcher variant="light" />
      </Box>

      {/* ── Floating icons ── */}
      <FloatingIcons icons={LOGIN_ICONS} />

      {/* ══════════════════════════════════════════
          CARD
      ══════════════════════════════════════════ */}
      <Box sx={{
        position:"relative", zIndex:1,
        width:"100%", maxWidth:480,
        backgroundColor: WHITE,
        borderRadius:"28px",
        boxShadow:"0 32px 80px rgba(0,0,0,.45), 0 0 0 1px rgba(66,165,245,.12)",
        overflow:"hidden",
        animation: mounted ? `${cardEnter} .75s cubic-bezier(.22,1,.36,1) both` : "none",
      }}>

        {/* Top accent bar */}
        <Box sx={{
          height:"4px",
          background:`linear-gradient(90deg, ${BLUE}, ${BLUE_LIGHT}, ${BLUE})`,
          backgroundSize:"200% 100%",
          animation:"shimmer-bar 3s linear infinite",
          "@keyframes shimmer-bar":{
            "0%":  { backgroundPosition:"-200% 0" },
            "100%":{ backgroundPosition: "200% 0" },
          },
        }}/>

        <Box sx={{ px:{ xs:3, sm:5 }, py:{ xs:4, sm:5 } }}>

          {/* Logo */}
          <Box
            onClick={() => navigate("/")}
            sx={{
              display:"flex", alignItems:"center", gap:1.2,
              cursor:"pointer", mb:4, width:"fit-content",
              animation: mounted ? `${fadeUp} .6s ease .05s both` : "none",
            }}
          >
            <Box sx={{
              width:44, height:44, borderRadius:"13px",
              background:`linear-gradient(135deg,${BLUE},${BLUE_LIGHT})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"1.45rem",
              boxShadow:`0 6px 20px rgba(21,101,192,.4)`,
              animation:`${logoFloat} 4s ease-in-out infinite`,
            }}>🐾</Box>
            <Typography sx={{ fontWeight:800, fontSize:"1.45rem", color:NAVY, letterSpacing:"-.03em" }}>
              Dr.<Box component="span" sx={{ color:BLUE }}>Paw</Box>
            </Typography>
          </Box>

          {/* Heading */}
          <Box sx={{ mb:4, animation: mounted ? `${fadeUp} .6s ease .12s both` : "none" }}>
            <Typography sx={{
              fontWeight:800, fontSize:{ xs:"1.8rem", sm:"2.2rem" },
              color:TEXT_DARK, letterSpacing:"-.025em", lineHeight:1.15, mb:.8,
              position:"relative", display:"inline-block",
              "&::after":{
                content:'""', position:"absolute",
                bottom:-5, left:0, height:"3px", borderRadius:"2px",
                background:`linear-gradient(90deg,${BLUE},transparent)`,
                animation: mounted ? `${underlineDraw} .7s ease .65s both` : "none",
              },
            }}>
              {tl.title}
            </Typography>
            <Typography sx={{ color:TEXT_MUTED, fontSize:".95rem", lineHeight:1.7 }}>
              {tl.subtitle}
            </Typography>
          </Box>

          {/* Alerts */}
          {sessionExpired && (
            <Alert severity="warning" sx={{ mb:3, borderRadius:"12px", animation:`${fadeUp} .4s ease both` }}>
              {tl.sessionExpired}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb:3, borderRadius:"12px", animation:`${fadeUp} .4s ease both` }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box
            component="form" onSubmit={handleSubmit} noValidate
            sx={{ display:"flex", flexDirection:"column", gap:2.5 }}
          >
            {/* Email */}
            <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .18s both` : "none" }}>
              <TextField
                fullWidth label={tl.email} name="email" type="email"
                value={form.email} onChange={handleChange} required
                onFocus={() => setFocus("email")} onBlur={() => setFocus("")}
                sx={fieldSx(focus === "email")}
                InputProps={{ startAdornment:(
                  <InputAdornment position="start">
                    <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="email" ? BLUE : TEXT_MUTED }}>✉️</Box>
                  </InputAdornment>
                )}}
              />
            </Box>

            {/* Password */}
            <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .24s both` : "none" }}>
              <TextField
                fullWidth label={tl.password} name="password"
                type={showPw ? "text" : "password"}
                value={form.password} onChange={handleChange} required
                onFocus={() => setFocus("password")} onBlur={() => setFocus("")}
                sx={fieldSx(focus === "password")}
                InputProps={{
                  startAdornment:(
                    <InputAdornment position="start">
                      <Box sx={{ fontSize:".95rem", mr:.5, color: focus==="password" ? BLUE : TEXT_MUTED }}>🔒</Box>
                    </InputAdornment>
                  ),
                  endAdornment:(
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw(!showPw)} size="small"
                        sx={{ color:TEXT_MUTED, "&:hover":{ color:BLUE } }}>
                        {showPw ? <VisibilityOff fontSize="small"/> : <Visibility fontSize="small"/>}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Remember + Forgot */}
            <Box sx={{
              display:"flex", justifyContent:"space-between",
              alignItems:"center", flexWrap:"wrap", gap:1,
              animation: mounted ? `${fadeUp} .6s ease .3s both` : "none",
            }}>
              <FormControlLabel
                control={
                  <Checkbox name="rememberMe" checked={form.rememberMe} onChange={handleChange}
                    size="small" sx={{ color:BORDER, "&.Mui-checked":{ color:BLUE } }}/>
                }
                label={<Typography sx={{ fontSize:".85rem", color:TEXT_MID }}>{tl.rememberMe}</Typography>}
              />
              <Link component={RouterLink} to="/forgot-password" sx={{
                color:BLUE, fontSize:".85rem", fontWeight:700, textDecoration:"none",
                "&:hover":{ color:NAVY, textDecoration:"underline" },
              }}>
                {tl.forgotPassword}
              </Link>
            </Box>

            {/* Submit */}
            <Box sx={{ animation: mounted ? `${fadeUp} .6s ease .36s both` : "none" }}>
              <Button
                fullWidth type="submit" variant="contained" disabled={loading}
                sx={{
                  py:1.85, borderRadius:"14px", fontSize:"1rem", fontWeight:800,
                  textTransform:"none", letterSpacing:".01em",
                  background:`linear-gradient(135deg,${BLUE},${BLUE_MID})`,
                  boxShadow:`0 8px 24px rgba(21,101,192,.35)`,
                  position:"relative", overflow:"hidden",
                  "&::before":{
                    content:'""', position:"absolute",
                    top:0, width:"60%", height:"100%",
                    background:"linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent)",
                    animation:`${shimmerBtn} 2.5s ease-in-out infinite 1s`,
                  },
                  "&:hover":{ transform:"translateY(-2px)", boxShadow:`0 14px 36px rgba(21,101,192,.45)` },
                  "&:active":{ transform:"translateY(0)" },
                  transition:"all .3s cubic-bezier(.34,1.56,.64,1)",
                }}
              >
                {loading
                  ? <CircularProgress size={22} sx={{ color:WHITE }}/>
                  : <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                      {tl.loginButton} <ForwardIcon sx={{ fontSize:"1.1rem" }}/>
                    </Box>
                }
              </Button>
            </Box>
          </Box>

          {/* Footer link */}
          <Box sx={{
            mt:3.5, textAlign:"center",
            animation: mounted ? `${fadeUp} .6s ease .42s both` : "none",
          }}>
            <Typography sx={{ color:TEXT_MUTED, fontSize:".9rem" }}>
              {tl.noAccount}{" "}
              <Link component={RouterLink} to="/signup" sx={{
                color:BLUE, fontWeight:800, textDecoration:"none",
                "&:hover":{ color:NAVY, textDecoration:"underline" },
              }}>
                {tl.signupLink}
              </Link>
            </Typography>
          </Box>

        </Box>
      </Box>
    </Box>
    </>
  );
};

export default Login;
